import React, {useRef, useState} from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {OverlayPanel} from "primereact/overlaypanel";
import {RadioButton} from "primereact/radiobutton";
import {SelectButton} from "primereact/selectbutton";
import {CUSTOMER_FIELDS, FIELD_TYPES, MAPPING_POLICIES} from "./engine";
import {FieldInput} from "./form-renderer";
import {ClearableDropdown, DROPDOWN_PT, Icon, PrimeDatePicker, useFormat, useT} from "./host";
import {Field, THEMES, themeKey, themeOf} from "./forms-ui";

// Step 1 (design), laid out as the boss's prototype: the form on a canvas exactly as respondents see it (desktop or mobile
// width), titles edited in place, an «add a question after this» row under every question opening a grid of question types,
// and the inspector beside it (question settings / form settings).
const ADD_TYPES = [
    "short_text", "long_text", "email", "phone", "number", "amount", "single_choice", "dropdown", "multi_choice", "yes_no", "rating",
    "date", "datetime", "file", "consent", "hidden", "heading", "paragraph", "divider",
];
export const TYPE_ICONS = {
    short_text: "short_text", long_text: "notes", email: "alternate_email", phone: "call", number: "tag", amount: "payments",
    single_choice: "radio_button_checked", dropdown: "arrow_drop_down_circle", multi_choice: "check_box", yes_no: "help", rating: "star",
    date: "calendar_today", datetime: "schedule", file: "attach_file", consent: "verified_user", hidden: "visibility_off",
    heading: "title", paragraph: "subject", divider: "horizontal_rule",
};
const INPUT = "w-full text-xs border-round-lg";
const DROPDOWN = "w-full text-xs border-round-lg";

export function newField(type, fields, t) {
    const used = new Set(fields.map((field) => field.key));
    let index = fields.length + 1;
    while (used.has(`q${index}`)) index += 1;
    const field = {key: `q${index}`, type, label: FIELD_TYPES[type]?.answer ? t("forms.builder.new-question") : t(`forms.field-type.${type}`)};
    if (FIELD_TYPES[type]?.options) field.options = [1, 2].map((n) => ({value: `o${n}`, label: t("forms.builder.option-n", {n})}));
    if (type === "rating") field.scale = 5;
    if (type === "consent") { field.label = t("forms.field-type.consent"); field.consentText = t("forms.builder.consent-default"); }
    if (type === "divider") field.label = "";
    return field;
}

// «add a question after this»: a dashed row that opens the grid of question types
function AddQuestion({onAdd, first}) {
    const t = useT();
    const panel = useRef(null);
    return (
        <>
            <Button type="button" text className="w-full text-xs gap-2 justify-content-center border-1 border-dashed border-300 border-round-xl py-2 text-primary"
                    icon={<Icon icon="mi mi:add" className="text-base"/>} label={t(first ? "forms.builder.add-first" : "forms.builder.add-after")}
                    onClick={(event) => panel.current?.toggle(event)}/>
            <OverlayPanel ref={panel} className="rtl" pt={{content: () => ({className: "p-2"})}}>
                <div className="grid m-0 w-20rem md:w-28rem">
                    {ADD_TYPES.map((type) => (
                        <div key={type} className="col-6 md:col-4 p-1">
                            <Button type="button" outlined severity="secondary" className="w-full text-xs gap-2 justify-content-start border-200 text-700 border-round-lg py-2"
                                    icon={<Icon icon={`mi mi:${TYPE_ICONS[type]}`} className="text-base text-primary"/>} label={t(`forms.field-type.${type}`)}
                                    onClick={() => { panel.current?.hide(); onAdd(type); }}/>
                        </div>
                    ))}
                </div>
            </OverlayPanel>
        </>
    );
}

// a quiet chip, as the owner chips on the customers page (Tag severities are loud brand fills in this theme)
function Chip({label, icon, tone = "surface-100 text-600"}) {
    return (
        <span className={`inline-flex align-items-center gap-1 border-round-2xl px-2 py-1 text-xs ${tone}`}>
            {icon && <Icon icon={`mi mi:${icon}`} className="text-sm"/>}{label}
        </span>
    );
}

const INLINE_TITLE = "w-full border-none shadow-none bg-transparent p-0 font-bold text-900";

function QuestionCard({field, index, total, selected, readOnly, theme, rulesByField, onSelect, onField, onMove, onDuplicate, onDelete}) {
    const t = useT();
    const layout = !FIELD_TYPES[field.type]?.answer;
    return (
        <div onClick={() => onSelect(field.key)}
             className={`border-1 border-round-xl p-3 cursor-pointer flex flex-column gap-2 ${selected ? `${theme.accent} ${theme.tint}` : "border-200 surface-card hover:border-primary"}`}>
            <div className="flex align-items-start gap-2">
                <div className="flex-1 min-w-0 flex flex-column gap-1">
                    {field.type === "divider" ? <div className="border-top-1 border-200 my-2"/>
                        : selected && !readOnly
                            ? <InputText className={`${INLINE_TITLE} ${field.type === "heading" ? "text-base" : "text-sm"}`} value={field.label || ""} placeholder={t("forms.builder.no-label")}
                                         onClick={(event) => event.stopPropagation()} onChange={(event) => onField({...field, label: event.target.value})}/>
                            : <strong className={field.type === "paragraph" ? "text-xs font-normal text-600 line-height-3 white-space-pre-line" : `${field.type === "heading" ? "text-base" : "text-sm"} text-800`}>
                                {field.label || t("forms.builder.no-label")}{field.required && <span className="text-red-500"> *</span>}
                            </strong>}
                    {field.help && !layout && <span className="text-xs text-500">{field.help}</span>}
                </div>
                {!readOnly && selected && (
                    <div className="flex gap-1 flex-none" onClick={(event) => event.stopPropagation()}>
                        <Button type="button" text rounded className="p-1" disabled={index === 0} icon={<Icon icon="mi mi:arrow_upward" className="text-base"/>} aria-label={t("forms.builder.move-up")} onClick={() => onMove(-1)}/>
                        <Button type="button" text rounded className="p-1" disabled={index === total - 1} icon={<Icon icon="mi mi:arrow_downward" className="text-base"/>} aria-label={t("forms.builder.move-down")} onClick={() => onMove(1)}/>
                        <Button type="button" text rounded className="p-1" icon={<Icon icon="mi mi:content_copy" className="text-base"/>} aria-label={t("forms.builder.duplicate")} onClick={onDuplicate}/>
                        <Button type="button" text rounded severity="danger" className="p-1" icon={<Icon icon="mi mi:delete" className="text-base"/>} aria-label={t("forms.builder.delete")} onClick={onDelete}/>
                    </div>
                )}
            </div>
            {!layout && field.type !== "hidden" && (
                <div className="pointer-events-none">
                    <FieldInput field={field} value={undefined} onChange={() => {}} theme={theme} disabled/>
                </div>
            )}
            {(field.type === "hidden" || field.mapping?.target || rulesByField[field.key] || field.sensitive) && (
                <div className="flex flex-wrap align-items-center gap-1">
                    {field.type === "hidden" && <Chip icon="visibility_off" label={t("forms.field-type.hidden")}/>}
                    {field.mapping?.target && <Chip tone="text-primary border-1 border-primary" icon="person" label={t(`forms.customer-field.${field.mapping.target}`)}/>}
                    {rulesByField[field.key] && <Chip tone="bg-yellow-50 text-yellow-700" icon="account_tree" label={t("forms.builder.conditional")}/>}
                    {field.sensitive && <Chip tone="bg-red-50 text-red-600" icon="lock" label={t("forms.builder.sensitive")}/>}
                </div>
            )}
        </div>
    );
}

export function Canvas({draft, selected, onSelect, onChange, readOnly, rulesByField, onPreview}) {
    const t = useT();
    const [device, setDevice] = useState("desktop");
    const fields = draft.fields || [];
    const meta = draft.meta || {};
    const theme = themeOf(meta);
    const setFields = (next) => onChange({...draft, fields: next});
    const insertAt = (index, type) => {
        const item = newField(type, fields, t);
        const next = [...fields];
        next.splice(index, 0, item);
        setFields(next);
        onSelect(item.key);
    };
    const move = (index, delta) => {
        const next = [...fields];
        const [item] = next.splice(index, 1);
        next.splice(index + delta, 0, item);
        setFields(next);
    };
    return (
        <div className={`flex-1 min-w-0 overflow-auto ${theme.soft} p-3 md:p-4 flex flex-column gap-3`}>
            <div className={`flex flex-wrap align-items-center justify-content-between gap-2 w-full mx-auto ${device === "mobile" ? "md:w-25rem" : "xl:w-9"}`}>
                <div className="flex flex-column gap-1">
                    <strong className="text-sm text-800">{t("forms.builder.canvas-title")}</strong>
                    <span className="text-xs text-500">{t("forms.builder.canvas-hint")}</span>
                </div>
                <div className="flex gap-2 align-items-center">
                    <SelectButton className="ltr flex text-xs" pt={{button: () => ({className: "text-xs py-2 px-3"})}} value={device} allowEmpty={false} onChange={(event) => setDevice(event.value)}
                                  options={[{value: "mobile", label: t("forms.builder.device-mobile")}, {value: "desktop", label: t("forms.builder.device-desktop")}]}/>
                    <Button type="button" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:visibility" className="text-base"/>} label={t("forms.preview.button")} onClick={onPreview}/>
                </div>
            </div>
            <div className={`w-full mx-auto surface-card border-1 border-200 border-round-2xl border-top-3 ${theme.accent} p-3 md:p-4 flex flex-column gap-3 ${device === "mobile" ? "md:w-25rem" : "xl:w-9"}`}>
                <div onClick={() => onSelect("__form")} className={`border-round-xl p-2 cursor-pointer flex flex-column gap-1 ${selected === "__form" ? theme.tint : ""}`}>
                    {meta.brandName && <span className="text-xs font-bold text-600">{meta.brandName}</span>}
                    {readOnly
                        ? <strong className="text-2xl text-900">{meta.title || t("forms.untitled")}</strong>
                        : <InputText className={`${INLINE_TITLE} text-2xl`} value={meta.title || ""} placeholder={t("forms.untitled")} onChange={(event) => onChange({...draft, meta: {...meta, title: event.target.value}})}/>}
                    {readOnly
                        ? <span className="text-xs text-500 line-height-3 white-space-pre-line">{meta.description}</span>
                        : <InputTextarea autoResize rows={1} className="w-full border-none shadow-none bg-transparent p-0 text-xs text-600 line-height-3" value={meta.description || ""} placeholder={t("forms.builder.description-placeholder")}
                                         onChange={(event) => onChange({...draft, meta: {...meta, description: event.target.value}})}/>}
                </div>
                {fields.length === 0 && !readOnly && <AddQuestion first onAdd={(type) => insertAt(0, type)}/>}
                {fields.map((field, index) => (
                    <React.Fragment key={field.key}>
                        <QuestionCard field={field} index={index} total={fields.length} selected={selected === field.key} readOnly={readOnly} theme={theme} rulesByField={rulesByField}
                                      onSelect={onSelect} onField={(next) => setFields(fields.map((item) => (item.key === field.key ? next : item)))} onMove={(delta) => move(index, delta)}
                                      onDuplicate={() => {
                                          const copy = {...JSON.parse(JSON.stringify(field)), key: newField(field.type, fields, t).key, mapping: undefined};
                                          const next = [...fields];
                                          next.splice(index + 1, 0, copy);
                                          setFields(next);
                                          onSelect(copy.key);
                                      }}
                                      onDelete={() => { setFields(fields.filter((item) => item.key !== field.key)); onSelect(null); }}/>
                        {!readOnly && <AddQuestion onAdd={(type) => insertAt(index + 1, type)}/>}
                    </React.Fragment>
                ))}
                <div className="flex align-items-center justify-content-between gap-2 pt-2">
                    <Button type="button" severity={theme.severity} className="text-sm gap-2 border-round-lg px-5 pointer-events-none" icon={<Icon icon="mi mi:send" className="text-base"/>} label={t("forms.public.submit")} tabIndex={-1}/>
                    <span className="text-xs text-400">{t("forms.public.made-with")}</span>
                </div>
            </div>
        </div>
    );
}

function Toggle({label, hint, checked, onChange, disabled}) {
    return (
        <label className="flex align-items-center justify-content-between gap-2 text-xs text-700">
            <span className="flex flex-column gap-1">{label}{hint && <span className="text-400">{hint}</span>}</span>
            <InputSwitch checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.value)}/>
        </label>
    );
}

function OptionsEditor({field, dimensions, onChange, readOnly}) {
    const t = useT();
    const options = field.options || [];
    const set = (index, patch) => onChange({...field, options: options.map((option, at) => (at === index ? {...option, ...patch} : option))});
    return (
        <Field label={t("forms.builder.options")} hint={dimensions.length ? t("forms.builder.options-scores-hint") : null}>
            <div className="flex flex-column gap-2">
                {options.map((option, index) => (
                    <div key={option.value} className="flex flex-column gap-1 surface-50 border-round-lg p-2">
                        <div className="flex gap-1 align-items-center">
                            <InputText className={INPUT} value={option.label} disabled={readOnly} onChange={(event) => set(index, {label: event.target.value})}/>
                            <Button type="button" text rounded severity="danger" className="p-1 flex-none" icon={<Icon icon="pi pi-times" className="text-sm"/>} disabled={readOnly || options.length <= 1}
                                    aria-label={t("forms.builder.remove-option")} onClick={() => onChange({...field, options: options.filter((_, at) => at !== index)})}/>
                        </div>
                        {dimensions.map((dimension) => (
                            <div key={dimension.key} className="flex align-items-center gap-2 text-xs">
                                <span className="text-500 flex-1">{dimension.label}</span>
                                <InputNumber value={option.scores?.[dimension.key] ?? null} disabled={readOnly} inputClassName="w-4rem text-xs" min={-100} max={100}
                                             onValueChange={(event) => set(index, {scores: {...(option.scores || {}), [dimension.key]: event.value}})}/>
                            </div>
                        ))}
                    </div>
                ))}
                {!readOnly && (
                    <Button type="button" text className="text-xs gap-2 align-self-start" icon={<Icon icon="mi mi:add" className="text-base"/>} label={t("forms.builder.add-option")} onClick={() => {
                        const used = new Set(options.map((option) => option.value));
                        let index = options.length + 1;
                        while (used.has(`o${index}`)) index += 1;
                        onChange({...field, options: [...options, {value: `o${index}`, label: t("forms.builder.option-n", {n: index})}]});
                    }}/>
                )}
            </div>
        </Field>
    );
}

export function FieldInspector({field, draft, onChange, readOnly}) {
    const t = useT();
    const format = useFormat();
    const set = (patch) => onChange({...field, ...patch});
    const kind = FIELD_TYPES[field.type]?.answer;
    const customer = draft.destination?.type === "customer";
    const dimensions = draft.scoreDimensions || [];
    const validation = field.validation || {};
    const setValidation = (patch) => set({validation: {...validation, ...patch}});
    if (!kind) {
        if (field.type === "divider") return <span className="text-xs text-500">{t("forms.builder.divider-help")}</span>;
        return (
            <Field label={t("forms.builder.text")}>
                <InputTextarea className={INPUT} autoResize rows={3} value={field.label || ""} disabled={readOnly} onChange={(event) => set({label: event.target.value})}/>
            </Field>
        );
    }
    return (
        <>
            <Field label={t("forms.builder.question")} required><InputText className={INPUT} value={field.label || ""} disabled={readOnly} onChange={(event) => set({label: event.target.value})}/></Field>
            <Field label={t("forms.builder.help")}><InputText className={INPUT} value={field.help || ""} placeholder={t("forms.optional")} disabled={readOnly} onChange={(event) => set({help: event.target.value})}/></Field>
            {["short_text", "long_text", "email", "phone", "number", "amount", "dropdown"].includes(field.type) && (
                <Field label={t("forms.builder.placeholder")}><InputText className={INPUT} value={field.placeholder || ""} disabled={readOnly} onChange={(event) => set({placeholder: event.target.value})}/></Field>
            )}
            <Field label={t("forms.builder.type")}>
                <Dropdown className={DROPDOWN} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={field.type} disabled={readOnly}
                          options={Object.keys(FIELD_TYPES).filter((type) => FIELD_TYPES[type].answer).map((value) => ({value, label: t(`forms.field-type.${value}`)}))}
                          onChange={(event) => {
                              const next = {...field, type: event.value};
                              if (FIELD_TYPES[event.value].options && !next.options) next.options = [1, 2].map((n) => ({value: `o${n}`, label: t("forms.builder.option-n", {n})}));
                              if (event.value === "rating" && !next.scale) next.scale = 5;
                              onChange(next);
                          }}/>
            </Field>
            {field.type !== "hidden" && <Toggle label={t("forms.builder.required")} checked={field.required} disabled={readOnly} onChange={(value) => set({required: value})}/>}
            <Toggle label={t("forms.builder.sensitive")} hint={t("forms.builder.sensitive-hint")} checked={field.sensitive} disabled={readOnly} onChange={(value) => set({sensitive: value})}/>
            {FIELD_TYPES[field.type]?.options && <OptionsEditor field={field} dimensions={dimensions} onChange={onChange} readOnly={readOnly}/>}
            {field.type === "rating" && (
                <>
                    <Field label={t("forms.builder.scale")}>
                        <SelectButton className="ltr flex text-xs" pt={{button: () => ({className: "flex-1 text-xs py-2"})}} value={field.scale || 5} disabled={readOnly} allowEmpty={false}
                                      options={[3, 5, 7, 10].map((value) => ({value, label: format.number(value)}))} onChange={(event) => set({scale: event.value})}/>
                    </Field>
                    <Field label={t("forms.builder.scale-labels")}>
                        <div className="flex gap-2">
                            <InputText className={INPUT} placeholder={t("forms.builder.scale-low")} value={field.scaleLabels?.[0] || ""} disabled={readOnly} onChange={(event) => set({scaleLabels: [event.target.value, field.scaleLabels?.[1] || ""]})}/>
                            <InputText className={INPUT} placeholder={t("forms.builder.scale-high")} value={field.scaleLabels?.[1] || ""} disabled={readOnly} onChange={(event) => set({scaleLabels: [field.scaleLabels?.[0] || "", event.target.value]})}/>
                        </div>
                    </Field>
                    {dimensions.length > 0 && (
                        <Field label={t("forms.builder.rating-dimensions")}>
                            {dimensions.map((dimension) => (
                                <label key={dimension.key} className="flex align-items-center gap-2 text-xs">
                                    <Checkbox disabled={readOnly} checked={(field.scoreDimensions || []).includes(dimension.key)}
                                              onChange={(event) => set({scoreDimensions: event.checked ? [...(field.scoreDimensions || []), dimension.key] : (field.scoreDimensions || []).filter((key) => key !== dimension.key)})}/>
                                    {dimension.label}
                                </label>
                            ))}
                        </Field>
                    )}
                </>
            )}
            {field.type === "consent" && <Field label={t("forms.builder.consent-text")}><InputTextarea className={INPUT} autoResize rows={2} value={field.consentText || ""} disabled={readOnly} onChange={(event) => set({consentText: event.target.value})}/></Field>}
            {kind === "number" && (
                <Field label={t("forms.builder.range")}>
                    <div className="flex gap-2">
                        <InputNumber placeholder={t("forms.builder.min")} value={validation.min ?? null} disabled={readOnly} onValueChange={(event) => setValidation({min: event.value})} className="flex-1" inputClassName={INPUT}/>
                        <InputNumber placeholder={t("forms.builder.max")} value={validation.max ?? null} disabled={readOnly} onValueChange={(event) => setValidation({max: event.value})} className="flex-1" inputClassName={INPUT}/>
                    </div>
                </Field>
            )}
            {["short_text", "long_text"].includes(field.type) && (
                <Field label={t("forms.builder.max-length")}>
                    <InputNumber value={validation.maxLength ?? null} min={1} max={5000} disabled={readOnly} onValueChange={(event) => setValidation({maxLength: event.value})} inputClassName={INPUT}/>
                </Field>
            )}
            {field.type === "file" && (
                <Field label={t("forms.builder.max-files")} hint={t("forms.builder.file-hint")}>
                    <InputNumber value={validation.maxFiles ?? 1} min={1} max={5} disabled={readOnly} onValueChange={(event) => setValidation({maxFiles: event.value})} inputClassName={INPUT}/>
                </Field>
            )}
            {customer && field.type !== "file" && (
                <div className="surface-50 border-1 border-200 border-round-xl p-3 flex flex-column gap-3">
                    <strong className="text-xs text-800">{t("forms.builder.mapping")}</strong>
                    <Field label={t("forms.builder.mapping-target")}>
                        <ClearableDropdown value={field.mapping?.target || null} disabled={readOnly} placeholder={t("forms.builder.no-mapping")} className="text-xs border-round-lg" panelClassName="rtl" pt={DROPDOWN_PT}
                                           options={CUSTOMER_FIELDS.map((value) => ({value, label: t(`forms.customer-field.${value}`)}))} clearAriaLabel={t("forms.builder.no-mapping")}
                                           onChange={(event) => set({mapping: event.value ? {target: event.value, policy: field.mapping?.policy || "fill_if_empty"} : undefined})}/>
                    </Field>
                    {field.mapping?.target && (
                        <Field label={t("forms.builder.mapping-policy")} hint={["primaryPhone", "primaryEmail"].includes(field.mapping.target) ? t("forms.builder.identity-hint") : null}>
                            <Dropdown className={DROPDOWN} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={field.mapping.policy || "fill_if_empty"} disabled={readOnly}
                                      options={MAPPING_POLICIES.map((value) => ({value, label: t(`forms.policy.${value}`)}))}
                                      onChange={(event) => set({mapping: {...field.mapping, policy: event.value}})}/>
                        </Field>
                    )}
                </div>
            )}
        </>
    );
}

// a radio card, as the prototype's choice cards (destination, ending)
export function ChoiceCard({selected, disabled, title, description, icon, onSelect}) {
    return (
        <Button type="button" text disabled={disabled} onClick={onSelect}
                className={`w-full h-full border-1 border-round-xl p-3 flex align-items-start gap-2 text-right ${selected ? "border-primary surface-50" : "border-200 surface-card"}`}>
            <RadioButton checked={selected} disabled={disabled} className="flex-none mt-1" onChange={onSelect}/>
            <span className="flex flex-column gap-1 min-w-0">
                <strong className="text-xs text-800 flex align-items-center gap-1">{icon && <Icon icon={`mi mi:${icon}`} className="text-base text-primary"/>}{title}</strong>
                {description && <span className="text-xs text-500 line-height-3">{description}</span>}
            </span>
        </Button>
    );
}

// the inspector's «settings» tab: look and behaviour of the form, what happens after submitting, who can respond
export function FormSettings({draft, onChange, readOnly}) {
    const t = useT();
    const meta = draft.meta || {};
    const access = draft.access || {};
    const ending = draft.endings?.default || {type: "message"};
    const setMeta = (patch) => onChange({...draft, meta: {...meta, ...patch}});
    const setAccess = (patch) => onChange({...draft, access: {...access, ...patch}});
    const setEnding = (patch) => onChange({...draft, endings: {...(draft.endings || {}), default: {...ending, ...patch}}});
    const current = themeKey(meta);
    return (
        <>
            <div className="flex flex-column gap-1">
                <strong className="text-xs text-800">{t("forms.settings.look-title")}</strong>
                <span className="text-xs text-400">{t("forms.settings.look-hint")}</span>
            </div>
            <Field label={t("forms.settings.theme")}>
                <div className="grid m-0">
                    {Object.keys(THEMES).map((key) => (
                        <div key={key} className="col-6 p-1">
                            <Button type="button" text disabled={readOnly} onClick={() => setMeta({theme: {...(meta.theme || {}), preset: key}})}
                                    className={`w-full border-1 border-round-lg p-2 gap-2 justify-content-start text-xs text-700 ${current === key ? "border-primary surface-50" : "border-200"}`}>
                                <span className={`w-1rem h-1rem border-round flex-none ${THEMES[key].swatch}`}/>{t(`forms.theme.${key}`)}
                            </Button>
                        </div>
                    ))}
                </div>
            </Field>
            <Field label={t("forms.settings.brand-name")} hint={t("forms.settings.brand-name-hint")}>
                <InputText className={INPUT} value={meta.brandName || ""} placeholder={t("forms.settings.brand-name-placeholder")} disabled={readOnly} onChange={(event) => setMeta({brandName: event.target.value})}/>
            </Field>
            <Toggle label={t("forms.settings.progress")} checked={meta.showProgress} disabled={readOnly} onChange={(value) => setMeta({showProgress: value})}/>

            <div className="border-top-1 border-200 pt-3 flex flex-column gap-2">
                <strong className="text-xs text-800">{t("forms.settings.after-submit")}</strong>
                {["message", "redirect", "page"].map((type) => (
                    <ChoiceCard key={type} selected={(ending.type || "message") === type} disabled={readOnly} title={t(`forms.ending.type.${type}`)}
                                description={t(`forms.ending.type-hint.${type}`)} onSelect={() => setEnding({type})}/>
                ))}
            </div>
            {ending.type !== "redirect" && (
                <>
                    <Field label={t("forms.settings.ending-title")}><InputText className={INPUT} value={ending.title || ""} placeholder={t("forms.ending.default-title")} disabled={readOnly} onChange={(event) => setEnding({title: event.target.value})}/></Field>
                    <Field label={t("forms.settings.ending-message")}><InputTextarea className={INPUT} autoResize rows={3} value={ending.message || ""} placeholder={t("forms.ending.default-message")} disabled={readOnly} onChange={(event) => setEnding({message: event.target.value})}/></Field>
                </>
            )}
            {ending.type === "redirect" && (
                <>
                    <Field label={t("forms.settings.redirect-url")} hint={t("forms.settings.https-only")}><InputText dir="ltr" className={INPUT} value={ending.url || ""} placeholder="https://" disabled={readOnly} onChange={(event) => setEnding({url: event.target.value})}/></Field>
                    <label className="flex align-items-center gap-2 text-xs"><Checkbox checked={Boolean(ending.newTab)} disabled={readOnly} onChange={(event) => setEnding({newTab: event.checked})}/>{t("forms.settings.new-tab")}</label>
                </>
            )}
            {ending.type === "page" && (
                <>
                    <Field label={t("forms.settings.button-label")}><InputText className={INPUT} value={ending.buttonLabel || ""} disabled={readOnly} onChange={(event) => setEnding({buttonLabel: event.target.value})}/></Field>
                    <Field label={t("forms.settings.button-url")} hint={t("forms.settings.https-only")}><InputText dir="ltr" className={INPUT} placeholder="https://" value={ending.buttonUrl || ""} disabled={readOnly} onChange={(event) => setEnding({buttonUrl: event.target.value})}/></Field>
                </>
            )}

            <div className="border-top-1 border-200 pt-3 flex flex-column gap-3">
                <strong className="text-xs text-800">{t("forms.settings.access")}</strong>
                <Field label={t("forms.settings.who")}>
                    <Dropdown className={DROPDOWN} panelClassName="rtl text-xs" pt={DROPDOWN_PT} value={access.mode || "public"} disabled={readOnly} onChange={(event) => setAccess({mode: event.value})}
                              options={["public", "internal", "both"].map((value) => ({value, label: t(`forms.access.${value}`)}))}/>
                </Field>
                <Field label={t("forms.settings.max-submissions")} hint={t("forms.settings.max-submissions-hint")}>
                    <InputNumber value={access.maxSubmissions ?? null} min={1} placeholder={t("forms.settings.unlimited")} disabled={readOnly} onValueChange={(event) => setAccess({maxSubmissions: event.value})} inputClassName={INPUT}/>
                </Field>
                <Field label={t("forms.settings.expires")}>
                    <div className="flex align-items-center gap-1">
                        <div className="flex-1">
                            <PrimeDatePicker inputClassName="w-full" placeholder={t("forms.settings.no-expiry")} value={access.expiresAt ? Math.floor(new Date(access.expiresAt).getTime() / 1000) : null}
                                             onChange={(event) => { if (readOnly) return; const day = new Date(event.value * 1000); day.setHours(23, 59, 59, 0); setAccess({expiresAt: day.toISOString()}); }}/>
                        </div>
                        <Button type="button" text severity="danger" className="p-1 flex-none" icon={<Icon icon="pi pi-times" className="text-sm"/>} disabled={readOnly || !access.expiresAt}
                                aria-label={t("forms.settings.clear-expiry")} onClick={() => setAccess({expiresAt: null})}/>
                    </div>
                </Field>
            </div>
        </>
    );
}
